/**
 * SybaSoil A/B test variant → cart attribute bridge
 * --------------------------------------------------
 * Gates on the IG test STARTED signal (window.SybaIG.whenResolved) — NOT
 * the raw ig-vars cookie — so PENDING customers always get pure Control UX.
 * window.SybaIG is provided by assets/syba-ig-test-state.js (loaded before
 * this file via sections/header.liquid).
 *
 * Why this exists:
 *   IG's Template Change inclusion criterion only fires on the Control
 *   template (PDP) — quick-add-only customers were never assigned, leaving
 *   their cart UX inconsistent with assigned PDP visitors. With IG's
 *   Styles & Javascript modification ALSO enabled on the test, IG actually
 *   does assign customers on non-PDP pages, but theme code never saw that
 *   assignment because the offer module's snippet (which sets the cart
 *   attribute on Variant B PDP render) never ran for these customers.
 *
 *   This bridge closes the gap: any page load on any URL — PDP or not —
 *   now propagates IG's STARTED assignment to the cart attribute. The cart
 *   drawer per-litre badge JS (assets/syba-cart-perliter-badge.js) reads
 *   the same attribute and renders correctly regardless of how the customer
 *   got to the cart.
 *
 * Test identifiers (from Intelligems API on 2026-05-15):
 *   Test UUID:                  7d2a2169-f16c-4d62-b8aa-802bcaac8d6c
 *   Cookie key (last 12 chars): 802bcaac8d6c
 *   Variant B (offer):          262f9d384523
 *   Control:                    f670b1f1b8d7
 *
 *   If the test ID ever changes (e.g. test deleted + recreated), update
 *   the constants in assets/syba-ig-test-state.js (single source of truth).
 *   The Intelligems MCP can list tests to re-fetch them — search for
 *   "Website - SYBASoil Offer" in the experiments list.
 *
 * Behaviour:
 *   - Test STARTED + Variant B → set cart attribute `_sybasoil_offer_active=true`
 *   - Test STARTED + Control   → clear cart attribute (in case it was previously set)
 *   - Test PENDING / inactive  → ACTIVELY CLEAR cart attribute (same as Control) — NOT a no-op.
 *                                The attribute is sticky on the Shopify cart; leaving a stale
 *                                'true' (written by old cookie-based code) would keep the
 *                                Save%/L badge suppressed while the test is pending.
 *   - window.SybaIG not ready  → no-op (defensive; helper failed to load)
 *
 *   Idempotent within a session via sessionStorage flag — fires at most one
 *   /cart/update.js per page load, and only when resolved state actually changed.
 */

(function () {
  'use strict';

  // sessionStorage key tracking which IG state we last propagated.
  // Avoids redundant /cart/update.js calls when the state hasn't changed.
  var SESSION_KEY = 'syba_ig_bridge_last_state';

  function setCartAttribute(value) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: { '_sybasoil_offer_active': value } })
    });
  }

  function init() {
    // If the shared helper didn't load, do nothing (defensive)
    if (!window.SybaIG || typeof window.SybaIG.whenResolved !== 'function') return;

    window.SybaIG.whenResolved(function (state) {
      var desiredAttr;
      if (state === 'variant-b') {
        desiredAttr = 'true';
      } else {
        // 'control' or 'inactive' (test pending/unassigned) — clear the attribute.
        // Must be active, not a no-op: the attribute is sticky on the Shopify cart
        // and old code may have written 'true' there. Leaving it causes the
        // Save%/L badge to stay suppressed on carts that predate this bridge.
        desiredAttr = '';
      }

      // Skip if we've already propagated this exact state in this session
      try {
        if (sessionStorage.getItem(SESSION_KEY) === state) return;
      } catch (e) { /* sessionStorage may be unavailable, fall through */ }

      setCartAttribute(desiredAttr).then(function () {
        try { sessionStorage.setItem(SESSION_KEY, state); } catch (e) { /* non-fatal */ }
      }).catch(function () { /* non-fatal — next page load will retry */ });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
